import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Get all vendor orders
export const getVendorOrders = createAsyncThunk(
  "vendorOrders/getAll",
  async ({ status, search, dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch orders" }
      );
    }
  }
);

// Get single vendor order
export const getVendorOrder = createAsyncThunk(
  "vendorOrders/getOne",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch order" }
      );
    }
  }
);

// Confirm order
export const confirmOrder = createAsyncThunk(
  "vendorOrders/confirm",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders/${orderId}/confirm`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to confirm order" }
      );
    }
  }
);

// Ship order
export const shipOrder = createAsyncThunk(
  "vendorOrders/ship",
  async ({ orderId, trackingNumber, trackingLink }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders/${orderId}/ship`,
        { trackingNumber, trackingLink },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to ship order" }
      );
    }
  }
);

// Deliver order
export const deliverOrder = createAsyncThunk(
  "vendorOrders/deliver",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders/${orderId}/deliver`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to mark order as delivered" }
      );
    }
  }
);

// Get order stats
export const getOrderStats = createAsyncThunk(
  "vendorOrders/getStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/orders/stats/overview`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch order stats" }
      );
    }
  }
);

const vendorOrderSlice = createSlice({
  name: "vendorOrders",
  initialState: {
    orders: [],
    selectedOrder: null,
    stats: null,
    loading: false,
    error: null,
    actionLoading: false,
  },
  reducers: {
    clearOrderError: (state) => {
      state.error = null;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Orders
      .addCase(getVendorOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.error = null;
      })
      .addCase(getVendorOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch orders";
      })

      // Get Single Order
      .addCase(getVendorOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
        state.error = null;
      })
      .addCase(getVendorOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch order";
      })

      // Confirm Order
      .addCase(confirmOrder.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(confirmOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.orders.findIndex((o) => o._id === action.payload.order._id);
        if (index !== -1) {
          state.orders[index] = action.payload.order;
        }
        if (state.selectedOrder?._id === action.payload.order._id) {
          state.selectedOrder = action.payload.order;
        }
        state.error = null;
      })
      .addCase(confirmOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to confirm order";
      })

      // Ship Order
      .addCase(shipOrder.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(shipOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.orders.findIndex((o) => o._id === action.payload.order._id);
        if (index !== -1) {
          state.orders[index] = action.payload.order;
        }
        if (state.selectedOrder?._id === action.payload.order._id) {
          state.selectedOrder = action.payload.order;
        }
        state.error = null;
      })
      .addCase(shipOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to ship order";
      })

      // Deliver Order
      .addCase(deliverOrder.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deliverOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.orders.findIndex((o) => o._id === action.payload.order._id);
        if (index !== -1) {
          state.orders[index] = action.payload.order;
        }
        if (state.selectedOrder?._id === action.payload.order._id) {
          state.selectedOrder = action.payload.order;
        }
        state.error = null;
      })
      .addCase(deliverOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to deliver order";
      })

      // Get Order Stats
      .addCase(getOrderStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(getOrderStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch stats";
      });
  },
});

export const { clearOrderError, clearSelectedOrder } = vendorOrderSlice.actions;
export default vendorOrderSlice.reducer;
